import React, { useReducer, useEffect } from 'react';
import { AppState } from './domain/types';
import { getRandomChallenges } from './data/challenges';
import { getLessons } from './data/lessons';
import HomeScreen from './screens/HomeScreen';
import LessonSelectScreen from './screens/LessonSelectScreen';
import GameScreen from './screens/GameScreen';
import RewardScreen from './screens/RewardScreen';
import SpellingDragDrop from './components/SpellingDragDrop';
import SpellingTrace from './components/SpellingTrace';
import './App.css';

const getRandomLetters = () => {
  // Ensure we have exactly the letters for CATALEYA: C-A-T-A-L-E-Y-A
  const requiredLetters = ['C', 'A', 'T', 'A', 'L', 'E', 'Y', 'A'];
  // Add 6 more random letters to make 14 total (2 rows of 7)
  const extraLetters = ['B', 'D', 'F', 'G', 'H', 'I', 'J', 'K', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'U', 'V', 'W', 'X', 'Z'];
  const randomExtra = extraLetters.sort(() => Math.random() - 0.5).slice(0, 6);
  return [...requiredLetters, ...randomExtra].sort(() => Math.random() - 0.5);
};

type AppAction = 
  | { type: 'START_GAME' }
  | { type: 'ANSWER_CHALLENGE'; answer: number }
  | { type: 'NEXT_CHALLENGE' }
  | { type: 'COMPLETE_GAME' }
  | { type: 'RESTART_GAME' }
  | { type: 'GO_HOME' }
  | { type: 'GO_LESSON_SELECT' }
  | { type: 'SELECT_LESSON'; lessonIndex: number }
  | { type: 'START_SPELLING_GAME' }
  | { type: 'PLACE_LETTER'; position: number; letter: string }
  | { type: 'TRACE_LETTER'; letterIndex: number }
  | { type: 'COMPLETE_SPELLING' }
  | { type: 'NEXT_LESSON' }
  | { type: 'TOGGLE_REDUCED_MOTION' }
  | { type: 'LOAD_LAST_SESSION'; session: any };

const initialState: AppState = {
  currentScreen: 'home',
  currentLesson: 0,
  gameState: {
    currentChallenge: 0,
    challenges: [],
    score: 0,
    stars: 0,
    errors: 0,
    showHint: false,
    isCompleted: false,
    startTime: 0
  },
  spellingGameState: {
    currentLetter: 0,
    targetWord: 'CATALEYA',
    letters: getRandomLetters(),
    placedLetters: [null, null, null, null, null, null, null, null],
    isCompleted: false,
    startTime: 0,
    stars: 0
  },
  lastSession: null,
  reducedMotion: false,
  totalStars: 0
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'START_GAME':
      const lessons = getLessons();
      const firstLesson = lessons[0];
      
      if (firstLesson.mode === 'math') {
        const newChallenges = getRandomChallenges(5);
        return {
          ...state,
          currentScreen: 'game',
          currentLesson: 0,
          gameState: {
            currentChallenge: 0,
            challenges: newChallenges,
            score: 0,
            stars: 0,
            errors: 0,
            showHint: false,
            isCompleted: false,
            startTime: Date.now()
          }
        };
      } else {
        return {
          ...state,
          currentScreen: 'game',
          currentLesson: 0,
          spellingGameState: {
            ...state.spellingGameState,
            currentLetter: 0,
            letters: getRandomLetters(),
            placedLetters: [null, null, null, null, null, null, null, null],
            isCompleted: false,
            startTime: Date.now()
          }
        };
      }

    case 'ANSWER_CHALLENGE':
      const currentChallenge = state.gameState.challenges[state.gameState.currentChallenge];
      const isCorrect = action.answer === currentChallenge.correct;
      const newScore = isCorrect ? state.gameState.score + 1 : state.gameState.score;
      const newStars = isCorrect ? state.gameState.stars + 1 : state.gameState.stars;
      const newErrors = isCorrect ? 0 : state.gameState.errors + 1;
      const showHint = newErrors >= 2;

      return {
        ...state,
        gameState: {
          ...state.gameState,
          score: newScore,
          stars: newStars,
          errors: newErrors,
          showHint
        }
      };

    case 'NEXT_CHALLENGE':
      const nextChallenge = state.gameState.currentChallenge + 1;
      const isGameComplete = nextChallenge >= state.gameState.challenges.length;
      
      return {
        ...state,
        currentScreen: isGameComplete ? 'reward' : 'game',
        gameState: {
          ...state.gameState,
          currentChallenge: nextChallenge,
          isCompleted: isGameComplete,
          showHint: false,
          errors: 0
        }
      };

    case 'COMPLETE_GAME':
      const session: any = {
        dateISO: new Date().toISOString(),
        total: state.gameState.challenges.length,
        correct: state.gameState.score,
        stars: state.gameState.stars,
        durationSec: Math.floor((Date.now() - state.gameState.startTime) / 1000)
      };
      
      localStorage.setItem('princesa-cata-last-session', JSON.stringify(session));
      
      // Increment total stars and go to reward screen
      const newTotalStars = state.totalStars + 1;
      return { 
        ...state, 
        currentScreen: 'reward', 
        lastSession: session,
        totalStars: newTotalStars
      };

    case 'RESTART_GAME':
      return {
        ...state,
        currentScreen: 'home',
        currentLesson: 0,
        totalStars: 0
      };

    case 'GO_HOME':
      return {
        ...state,
        currentScreen: 'home'
      };

    case 'GO_LESSON_SELECT':
      return {
        ...state,
        currentScreen: 'lesson-select'
      };

    case 'SELECT_LESSON':
      const selectLessons = getLessons();
      const selectedLesson = selectLessons[action.lessonIndex];
      
      if (selectedLesson.mode === 'math') {
        const newChallenges = getRandomChallenges(5);
        return {
          ...state,
          currentScreen: 'game',
          currentLesson: action.lessonIndex,
          gameState: {
            currentChallenge: 0,
            challenges: newChallenges,
            score: 0,
            stars: 0,
            errors: 0,
            showHint: false,
            isCompleted: false,
            startTime: Date.now()
          }
        };
      } else {
        return {
          ...state,
          currentScreen: 'game',
          currentLesson: action.lessonIndex,
          spellingGameState: {
            ...state.spellingGameState,
            currentLetter: 0,
            letters: getRandomLetters(),
            placedLetters: [null, null, null, null, null, null, null, null],
            isCompleted: false,
            startTime: Date.now()
          }
        };
      }

    case 'PLACE_LETTER':
      const targetWord = state.spellingGameState.targetWord;
      const targetLetters = targetWord.split('');
      const correctLetter = targetLetters[action.position];
      
      // Check if the letter is correct for this position
      if (action.letter === correctLetter) {
        // Correct letter - place it
        const newPlacedLetters = [...state.spellingGameState.placedLetters];
        newPlacedLetters[action.position] = action.letter;
        
        return {
          ...state,
          spellingGameState: {
            ...state.spellingGameState,
            placedLetters: newPlacedLetters,
            stars: state.spellingGameState.stars + 1
          }
        };
      } else {
        // Wrong letter - don't place it, return to available pool
        return state;
      }

    case 'TRACE_LETTER':
      return {
        ...state,
        spellingGameState: {
          ...state.spellingGameState,
          currentLetter: action.letterIndex + 1,
          stars: state.spellingGameState.stars + 1
        }
      };

    case 'COMPLETE_SPELLING':
      // Increment total stars and go to reward screen
      const newTotalStarsSpelling = state.totalStars + 1;
      return {
        ...state,
        currentScreen: 'reward',
        spellingGameState: {
          ...state.spellingGameState,
          isCompleted: true
        },
        totalStars: newTotalStarsSpelling
      };

    case 'NEXT_LESSON':
      // Check if there are more lessons
      const allLessons = getLessons();
      const nextLessonIndex = state.currentLesson + 1;
      
      if (nextLessonIndex < allLessons.length) {
        const nextLesson = allLessons[nextLessonIndex];
        
        if (nextLesson.mode === 'math') {
          const newChallenges = getRandomChallenges(5);
          return {
            ...state,
            currentScreen: 'game',
            currentLesson: nextLessonIndex,
            gameState: {
              currentChallenge: 0,
              challenges: newChallenges,
              score: 0,
              stars: 0,
              errors: 0,
              showHint: false,
              isCompleted: false,
              startTime: Date.now()
            }
          };
        } else {
          return {
            ...state,
            currentScreen: 'game',
            currentLesson: nextLessonIndex,
            spellingGameState: {
              ...state.spellingGameState,
              currentLetter: 0,
              letters: getRandomLetters(),
              placedLetters: [null, null, null, null, null, null, null, null],
              isCompleted: false,
              startTime: Date.now(),
              stars: 0
            }
          };
        }
      } else {
        // No more lessons - stay on reward screen
        return state;
      }

    case 'TOGGLE_REDUCED_MOTION':
      return {
        ...state,
        reducedMotion: !state.reducedMotion
      };

    case 'LOAD_LAST_SESSION':
      return {
        ...state,
        lastSession: action.session
      };

    default:
      return state;
  }
}

function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    // Cargar última sesión al iniciar
    const lastSession = localStorage.getItem('princesa-cata-last-session');
    if (lastSession) {
      try {
        const session = JSON.parse(lastSession);
        dispatch({ type: 'LOAD_LAST_SESSION', session });
      } catch (error) {
        console.error('Error loading last session:', error);
      }
    }

    // Detectar preferencia de animaciones reducidas
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      dispatch({ type: 'TOGGLE_REDUCED_MOTION' });
    }
  }, []);

  const renderScreen = () => {
    const lessons = getLessons();
    const currentLesson = lessons[state.currentLesson];
    
    switch (state.currentScreen) {
      case 'home':
        return (
          <HomeScreen 
            onStartGame={() => dispatch({ type: 'START_GAME' })}
          />
        );
      case 'lesson-select':
        return (
          <LessonSelectScreen
            lessons={lessons}
            onSelectLesson={(lessonIndex) => dispatch({ type: 'SELECT_LESSON', lessonIndex })}
            onGoHome={() => dispatch({ type: 'GO_HOME' })}
          />
        );
      case 'game':
        if (currentLesson?.mode === 'spelling') {
          if (currentLesson.id === 'spelling-1') {
            // Drag and drop spelling
            return (
              <div className="spelling-game-screen">
                <SpellingDragDrop
                  gameState={state.spellingGameState}
                  onLetterPlaced={(position, letter) => 
                    dispatch({ type: 'PLACE_LETTER', position, letter })
                  }
                  onComplete={() => dispatch({ type: 'COMPLETE_SPELLING' })}
                  reducedMotion={state.reducedMotion}
                  totalStars={state.totalStars}
                />
              </div>
            );
          } else {
            // Trace spelling
            return (
              <div className="spelling-game-screen">
                <SpellingTrace
                  gameState={state.spellingGameState}
                  onLetterTraced={(letterIndex) => 
                    dispatch({ type: 'TRACE_LETTER', letterIndex })
                  }
                  onComplete={() => dispatch({ type: 'COMPLETE_SPELLING' })}
                  reducedMotion={state.reducedMotion}
                  totalStars={state.totalStars}
                />
              </div>
            );
          }
        } else {
          // Math game
          return (
            <GameScreen
              gameState={state.gameState}
              onAnswer={(answer) => dispatch({ type: 'ANSWER_CHALLENGE', answer })}
              onNext={() => dispatch({ type: 'NEXT_CHALLENGE' })}
              onComplete={() => dispatch({ type: 'COMPLETE_GAME' })}
              reducedMotion={state.reducedMotion}
              totalStars={state.totalStars}
            />
          );
        }
      case 'reward':
        return (
          <RewardScreen
            gameState={state.gameState}
            spellingGameState={state.spellingGameState}
            currentLesson={currentLesson}
            lastSession={state.lastSession}
            onRestart={() => dispatch({ type: 'RESTART_GAME' })}
            onGoHome={() => dispatch({ type: 'GO_HOME' })}
            totalStars={state.totalStars}
            onNextLesson={() => dispatch({ type: 'NEXT_LESSON' })}
          />
        );
      default:
        return (
          <HomeScreen 
            onStartGame={() => dispatch({ type: 'START_GAME' })}
          />
        );
    }
  };

  return (
    <div className="app">
      {renderScreen()}
    </div>
  );
}

export default App;
